import { PaginationControls } from "@/components/pagination-controls";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useServerPagination } from "@/hooks/use-pagination";
import { ContentOperationService } from "@/lib/api/content.operation.service";
import { useAuth } from "@/lib/auth";
import { withFromParam } from "@/lib/back-nav";
import { CONTENT_TYPES } from "@/lib/content-types";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  FileText,
  Info,
} from "lucide-react";
import { useLocation } from "wouter";
import { ISSUE_TYPE_LABELS } from "@/lib/studio-constants";

interface HealthIssue {
  id: string;
  contentType: string;
  title: string;
  issue: string;
  issueType: string;
  severity: "high" | "medium" | "low";
  editUrl: string;
  updatedAt: string | null;
}

interface HealthResponse {
  issues: HealthIssue[];
  total: number;
  issueCounts: Record<string, number>;
  severityCounts: Record<HealthIssue["severity"], number>;
}

const PAGE_SIZE = 5;

function ContentTypeIcon({ type }: { type: string }) {
  const Icon = CONTENT_TYPES[type as keyof typeof CONTENT_TYPES]?.icon ?? FileText;
  return <Icon className="h-4 w-4 text-muted-foreground" />;
}

function severityIcon(severity: string) {
  if (severity === "high")
    return <AlertCircle className="h-4 w-4 text-destructive" />;
  if (severity === "medium")
    return <AlertTriangle className="h-4 w-4 text-amber-500" />;
  return <Info className="h-4 w-4 text-muted-foreground" />;
}

export default function QualityPage() {
  const { user } = useAuth();
  const [location, setLocation] = useLocation();
  const isAdmin = Boolean(
    user?.roles?.includes("admin") ||
      user?.roles?.includes("super_admin") ||
      user?.roles?.includes("coach_manager"),
  );

  const [page, setPage] = useState(1);
  const { data: healthData, isLoading } = useQuery<HealthResponse>({
    queryKey: ["/content/health-issues", page],
    queryFn: () =>
      ContentOperationService.getHealthIssues({ page, limit: PAGE_SIZE }),
    placeholderData: keepPreviousData,
    enabled: isAdmin,
  });

  const pageIssues = healthData?.issues || [];
  const totalIssues = healthData?.total ?? 0;
  const issueCounts = healthData?.issueCounts || {};

  const paginationProps = useServerPagination({
    page,
    total: totalIssues,
    pageSize: PAGE_SIZE,
    onPageChange: setPage,
  });

  const highCount = healthData?.severityCounts?.high ?? 0;
  const mediumCount = healthData?.severityCounts?.medium ?? 0;
  const lowCount = healthData?.severityCounts?.low ?? 0;

  if (!isAdmin) {
    return (
      <div
        className="p-6 space-y-6 max-w-6xl mx-auto"
        data-testid="quality-page"
      >
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setLocation("/content-operations")}
            data-testid="button-back"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1
            className="text-2xl font-semibold tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="text-page-title"
          >
            Quality Monitor
          </h1>
        </div>
        <Card>
          <CardContent
            className="p-6 text-center text-muted-foreground"
            data-testid="text-access-denied"
          >
            Quality monitoring is available for Admin, Super Admin, and Coach
            Manager roles.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="quality-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setLocation("/content-operations")}
          data-testid="button-back"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1
            className="text-2xl font-semibold tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="text-page-title"
          >
            Quality Monitor
          </h1>
          <p className="text-sm text-muted-foreground">
            Identify and resolve content quality issues
          </p>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : totalIssues === 0 ? (
        <Card>
          <CardContent className="p-6 text-center" data-testid="text-no-issues">
            <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              All content is healthy. No issues detected.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card data-testid="card-severity-total">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Total Issues</p>
                <p className="text-2xl font-semibold tabular-nums">
                  {totalIssues}
                </p>
              </CardContent>
            </Card>
            <Card data-testid="card-severity-high">
              <CardContent className="p-4">
                <div className="flex items-center gap-1.5">
                  <AlertCircle className="h-4 w-4 text-destructive" />
                  <p className="text-sm text-muted-foreground">High</p>
                </div>
                <p className="text-2xl font-semibold tabular-nums text-destructive">
                  {highCount}
                </p>
              </CardContent>
            </Card>
            <Card data-testid="card-severity-medium">
              <CardContent className="p-4">
                <div className="flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <p className="text-sm text-muted-foreground">Medium</p>
                </div>
                <p className="text-2xl font-semibold tabular-nums text-amber-600 dark:text-amber-400">
                  {mediumCount}
                </p>
              </CardContent>
            </Card>
            <Card data-testid="card-severity-low">
              <CardContent className="p-4">
                <div className="flex items-center gap-1.5">
                  <Info className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Low</p>
                </div>
                <p className="text-2xl font-semibold tabular-nums">
                  {lowCount}
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {Object.entries(issueCounts).map(([issueType, count]) => (
              <Badge
                key={issueType}
                variant="outline"
                data-testid={`badge-issue-${issueType}`}
              >
                {ISSUE_TYPE_LABELS[issueType] || issueType}: {count}
              </Badge>
            ))}
            <span
              className="text-sm text-muted-foreground ml-auto tabular-nums"
              data-testid="text-issue-count"
            >
              {totalIssues} issues found
            </span>
          </div>

          <div className="border rounded-md overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">Severity</TableHead>
                  <TableHead className="w-[50px]">Type</TableHead>
                  <TableHead>Content</TableHead>
                  <TableHead>Issue</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageIssues.map((issue) => (
                  <TableRow
                    key={`${issue.id}-${issue.issueType}`}
                    data-testid={`row-quality-${issue.id}-${issue.issueType}`}
                  >
                    <TableCell>{severityIcon(issue.severity)}</TableCell>
                    <TableCell>
                      <ContentTypeIcon type={issue.contentType} />
                    </TableCell>
                    <TableCell
                      className="font-medium max-w-[200px] truncate"
                      data-testid={`text-quality-title-${issue.id}-${issue.issueType}`}
                    >
                      {issue.title || "Untitled"}
                    </TableCell>
                    <TableCell
                      className="text-sm text-muted-foreground"
                      data-testid={`text-quality-issue-${issue.id}-${issue.issueType}`}
                    >
                      {issue.issue}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() =>
                          setLocation(withFromParam(issue.editUrl, location))
                        }
                        data-testid={`button-fix-${issue.id}-${issue.issueType}`}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <PaginationControls
              currentPage={paginationProps.currentPage}
              totalPages={paginationProps.totalPages}
              totalItems={paginationProps.totalItems}
              startIndex={paginationProps.startIndex}
              endIndex={paginationProps.endIndex}
              canGoNext={paginationProps.canGoNext}
              canGoPrev={paginationProps.canGoPrev}
              onPageChange={paginationProps.setPage}
              onNext={paginationProps.nextPage}
              onPrev={paginationProps.prevPage}
            />
          </div>
        </>
      )}
    </div>
  );
}
